import { supabase } from '@/integrations/supabase/client';
import { failureFromResponse } from '@/utils/userFacingError';

/**
 * POSTs `{ action, ...data }` to an action-dispatching edge function as the signed-in user.
 * A failure throws an EdgeFunctionFailure carrying the server's `message`, status and code:
 * handleError()'s body is `{error: errorType, message, details}`, where `.error` is only a code
 * such as "CONFLICT". Render it with userFacingErrorMessage().
 */
export async function postEdgeFunctionAction<T = unknown>(
  functionName: string,
  action: string,
  data: Record<string, unknown> = {},
): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ action, ...data }),
    }
  );

  if (!response.ok) {
    throw await failureFromResponse(response, "Something went wrong. Please try again.");
  }

  return response.json();
}
